import { useState, useRef, useEffect } from "react";
import { DotsThreeVertical, FolderSimple } from "@phosphor-icons/react";

/**
 * RecordingActionsMenu
 * A small kebab-menu button with a "Move to Folder" action.
 * Props:
 *   onMoveToFolder()
 *   moveDisabled       — true when OneDrive isn't connected, disables the action
 *   moveDisabledReason — tooltip text shown when moveDisabled is true
 */
export default function RecordingActionsMenu({ onMoveToFolder, moveDisabled = false, moveDisabledReason = "" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={ref} onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-center w-7 h-7 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
        data-testid="recording-actions-menu-btn"
      >
        <DotsThreeVertical size={16} weight="bold" />
      </button>
      {open && (
        <div
          className="absolute right-0 top-8 z-20 bg-white border border-slate-200 shadow-lg rounded-md py-1 w-44"
          data-testid="recording-actions-menu"
        >
          <span title={moveDisabled ? moveDisabledReason : undefined}>
            <button
              onClick={() => { if (moveDisabled) return; setOpen(false); onMoveToFolder(); }}
              aria-disabled={moveDisabled}
              data-disabled={moveDisabled}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                moveDisabled
                  ? "text-slate-300 cursor-not-allowed"
                  : "text-slate-700 hover:bg-slate-50"
              }`}
              data-testid="recording-move-to-folder-action"
            >
              <FolderSimple size={14} className={moveDisabled ? "text-slate-200" : "text-slate-400"} /> Move to Folder
            </button>
          </span>
        </div>
      )}
    </div>
  );
}
